import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/seoData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.category-dropdown')) setDropdownOpen(false);
      if (!target.closest('.guide-dropdown')) setGuideDropdownOpen(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const guideLinks = [
    { title: 'Spreadsheet Guide', slug: 'sugargoo-spreadsheet-guide' },
    { title: 'Sugargoo Review', slug: 'sugargoo-review' },
    { title: 'Shipping Cost Guide', slug: 'sugargoo-shipping-cost' },
    { title: 'Coupon Guide', slug: 'sugargoo-coupon-guide' },
    { title: 'Beginners Guide', slug: 'sugargoo-beginners-guide' },
    { title: 'Is Sugargoo Legit?', slug: 'is-sugargoo-legit' },
    { title: 'Warehouse Guide', slug: 'sugargoo-warehouse-guide' },
    { title: 'Best Finds 2026', slug: 'best-sugargoo-finds' },
    { title: 'QC Photos Guide', slug: 'sugargoo-qc-photos' },
    { title: 'Product Database', slug: 'sugargoo-database' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-xl font-heading font-semibold text-foreground-900">
              Sugargoo <span className="text-primary-600">Spread</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-home-line mr-1"></i>Home
            </Link>

            <div className="category-dropdown relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 border border-background-200/70 rounded-lg shadow-sm py-2 w-60 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <div className="w-7 h-7 rounded-md overflow-hidden shrink-0 bg-background-200">
                        <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="guide-dropdown relative">
              <button
                onClick={() => setGuideDropdownOpen(!guideDropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line mr-1"></i>Shopping Guide
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 border border-background-200/70 rounded-lg shadow-sm py-2 w-56 max-h-80 overflow-y-auto z-50">
                  {guideLinks.map((link) => (
                    <Link
                      key={link.slug}
                      to={`/article/${link.slug}`}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-article-line text-base"></i>
                      {link.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap">
              Main Website
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 pb-6 max-h-[75vh] overflow-y-auto">
          <div className="flex flex-col gap-3 pt-3">
            <Link to="/" className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-2.5 border-b border-background-200/50" onClick={() => setMobileOpen(false)}>
              <i className="ri-home-line text-base"></i>Home
            </Link>

            <div className="py-1">
              <p className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-1.5">
                <i className="ri-grid-line text-sm"></i>Product Categories
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 text-xs text-foreground-600 hover:text-primary-600 py-2 px-1.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    <div className="w-6 h-6 rounded overflow-hidden shrink-0 bg-background-200">
                      <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="py-1 border-t border-background-200/50">
              <p className="text-sm font-semibold text-foreground-900 mb-2 mt-2 flex items-center gap-1.5">
                <i className="ri-book-open-line text-sm"></i>Shopping Guides
              </p>
              <div className="grid grid-cols-1 gap-0.5">
                {guideLinks.map((link) => (
                  <Link
                    key={link.slug}
                    to={`/article/${link.slug}`}
                    className="flex items-center gap-2 text-xs text-foreground-600 hover:text-primary-600 py-2 px-2 rounded-md hover:bg-background-100 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <i className="ri-article-line text-sm"></i>
                    {link.title}
                  </Link>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/50 pt-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-2"
              >
                <i className="ri-external-link-line text-sm"></i>Main Website
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 mt-2 px-4 py-2.5 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-bag-line"></i>Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}